import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import OvRecordEditDialog from '../molecules/OvRecordEditDialog';
import OvReplaceVideoForm from './OvReplaceVideoForm';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { getAxiosErrorMessage } from '../../../common/utils/helpers';
import { getHttpStatus } from '../../../common/utils/http-status';
import { useAdminVideoMutations } from '../../../hooks/use-admin-video-mutations';
import { useReplacementStaging } from '../../../hooks/use-replacement-staging';
import { useVideoUploads } from '../../../hooks/use-video-uploads';

interface OvReplaceVideoDialogProps {
  video: AdminVideo;
  isOpen: boolean;
  onClose: () => void;
}

const OvReplaceVideoDialog: FC<OvReplaceVideoDialogProps> = ({
  video,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const staging = useReplacementStaging(video);
  const { replace } = useAdminVideoMutations();
  const { enqueue } = useVideoUploads();
  const { staged, request } = staging;

  const close = () => {
    staging.clear();
    replace.reset();
    onClose();
  };

  const submit = () => {
    if (!staged || !request) {
      return;
    }

    replace.mutate(
      { id: video.id, request },
      {
        onSuccess: (upload) => {
          enqueue([
            { ...upload, file: staged.file, contentType: staged.contentType },
          ]);
          close();
        },
      }
    );
  };

  const getErrorMessage = () => {
    if (!replace.error) {
      return undefined;
    }

    return getHttpStatus(replace.error) === 409
      ? t('videos.replace.conflict')
      : getAxiosErrorMessage(replace.error);
  };

  return (
    <OvRecordEditDialog
      isOpen={isOpen}
      title={t('videos.replace.title', { title: video.title })}
      onClose={close}
      onSubmit={submit}
      isDirty={!!staged}
      isSaving={replace.isPending}
      errorMessage={getErrorMessage()}
      submitLabel={t('videos.replace.submit')}
      busyLabel={t('videos.replace.submitting')}
      isSubmitDisabled={!request}
    >
      <OvReplaceVideoForm
        video={video}
        staging={staging}
        isDisabled={replace.isPending}
      />
    </OvRecordEditDialog>
  );
};

export default OvReplaceVideoDialog;
